import {
  ActionIcon,
  Button,
  Group,
  Modal,
  ScrollArea,
  Table,
  Text,
  Tooltip,
  createStyles,
} from '@mantine/core';
import classNames from 'classnames';
import React, {useCallback, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/analytics-products/ModelHubAnalyticsConstants';
import {Section} from 'constants/NavConstants';
import {EXTERNAL_FILE_TYPE} from 'constants/PreviewTypeConstants';
import {isS3URLExpired} from 'external-files/SignedExtFileURLProxy';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {accessibleObjs, type AccessibleGraphQLObject} from 'graphql-client/InaccessibleInfo';
import navigateToURL from 'lib/navigateToURL';
import type {ProteinAminoAcidsQuery} from 'model-hub/__generated/ProteinAminoAcidsQuery';
import {PREDICTION_JOB_MOLECULE_SMILES_QUERY, PROTEIN_AMINO_ACIDS_QUERY} from 'model-hub/ModelHubQueries';
import {
  getRawComponentLabelPrefix,
  isAaSequenceIdInputComponent,
  isAminoAcidsInputComponent,
  isMoleculeIdInputComponent,
  isProteinIdInputComponent,
  isRawPredictionInputComponent,
  isSmilesInputComponent,
  resolvePredictionInputComponents,
  type PredictionInputComponent,
} from 'model-hub/scientific-models/input-forms/BulkPredictionUtils';
import {navigateToProteinStructureTab} from 'molbio/aa-sequences/alphafold/AlphaFoldTabUtils';
import MolstarViewer, {MOLSTAR_PREVIEW_TYPE_TO_FORMAT} from 'molbio/molstar/ViewerWrapper';
import {expandSection} from 'nav/sidepanel/SidepanelActions';
import getTestProps from 'test/lib/cypress/getTestProps';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import * as Colors from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import APIUtil from 'util/APIUtil';
import type {BasicEvent} from 'util/Types';

import type {InaccessibleInfoGraphQL} from '../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';

const WINDOW_OPEN_FEATURES = 'noopener,noreferrer';

type PredictionJobMoleculeNode = AccessibleGraphQLObject & {
  __typename: 'MoleculeNode';
  currentVersion: {
    chemicalStructure: {
      canonicalSmiles: string;
    };
  };
};

interface PredictionJobMoleculeSmilesQuery {
  molecules: Array<PredictionJobMoleculeNode | InaccessibleInfoGraphQL>;
}

type PreviewSequenceContentKind = 'amino_acids' | 'smiles';

interface PreviewSequenceDisplayItem {
  key: string;
  entityId: string | null;
  rawLabel: string | null;
  contentKind: PreviewSequenceContentKind;
  text: string;
}

function previewItemFromComponent(
  component: PredictionInputComponent,
  index: number,
  rawEntryIndex: number | null,
  useNumberedLabels: boolean,
  proteinAminoAcidsById: ReadonlyMap<string, string>,
  moleculeSmilesById: ReadonlyMap<string, string>
): PreviewSequenceDisplayItem {
  if (isAminoAcidsInputComponent(component)) {
    const rawLabel =
      rawEntryIndex != null
        ? getRawComponentLabelPrefix(component, rawEntryIndex, useNumberedLabels)
        : 'Sequence';
    return {
      key: `${index}-aminoAcids`,
      entityId: null,
      rawLabel,
      contentKind: 'amino_acids',
      text: component.value,
    };
  }
  if (isSmilesInputComponent(component)) {
    const rawLabel =
      rawEntryIndex != null
        ? getRawComponentLabelPrefix(component, rawEntryIndex, useNumberedLabels)
        : 'Ligand';
    return {
      key: `${index}-smiles`,
      entityId: null,
      rawLabel,
      contentKind: 'smiles',
      text: component.value,
    };
  }
  if (isAaSequenceIdInputComponent(component) || isProteinIdInputComponent(component)) {
    const entityId = component.id;
    const aminoAcids = proteinAminoAcidsById.get(entityId);
    return {
      key: `${index}-${component._type}`,
      entityId,
      rawLabel: null,
      contentKind: 'amino_acids',
      text: aminoAcids ?? getCouldNotLoadContentMessage('amino_acids'),
    };
  }
  if (isMoleculeIdInputComponent(component)) {
    const smiles = moleculeSmilesById.get(component.id);
    return {
      key: `${index}-molecule`,
      entityId: component.id,
      rawLabel: null,
      contentKind: 'smiles',
      text: smiles ?? getCouldNotLoadContentMessage('smiles'),
    };
  }
  return {
    key: `${index}-unknown`,
    entityId: null,
    rawLabel: 'Component',
    contentKind: 'amino_acids',
    text: '—',
  };
}

function handlePreviewChipClick(
  event: {stopPropagation: () => void},
  apiId: string,
  item: {editUrl?: string}
): void {
  event.stopPropagation();
  if (APIUtil.isProtein(apiId)) {
    navigateToProteinStructureTab(apiId).catch(() => {});
    return;
  }
  expandSection(Section.PROJECTS);
  if (item.editUrl != null) {
    navigateToURL(item.editUrl);
  }
}

function PreviewSequenceNameCell({item}: {item: PreviewSequenceDisplayItem}): JSX.Element {
  if (item.entityId != null) {
    return (
      <BenchChip
        id={item.entityId}
        size={ChipSize.SMALL}
        onClick={(event: BasicEvent, chipItem: {editUrl?: string}) => {
          handlePreviewChipClick(event, item.entityId!, chipItem);
        }}
      />
    );
  }
  return <span>{item.rawLabel ?? 'Component'}</span>;
}

function getCouldNotLoadContentMessage(contentKind: PreviewSequenceContentKind): string {
  if (contentKind === 'smiles') {
    if (!BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE) {
      return 'Molecule chemistry is not enabled on this tenant.';
    }
    return 'Could not load molecule SMILES.';
  }
  return 'Could not load protein sequence.';
}

async function resolvePreviewSequenceItems(
  componentApiIds: ReadonlyArray<string>,
  modelName: string,
  configJson?: Record<string, unknown> | null
): Promise<Array<PreviewSequenceDisplayItem>> {
  const components = resolvePredictionInputComponents(componentApiIds, configJson, modelName);

  const rawEntryCount = components.filter((component) => isRawPredictionInputComponent(component)).length;
  const useNumberedLabels = rawEntryCount > 1;

  const proteinIds = Array.from(
    new Set(
      components.flatMap((component) =>
        isAaSequenceIdInputComponent(component) || isProteinIdInputComponent(component) ? [component.id] : []
      )
    )
  );
  const moleculeIds = Array.from(
    new Set(components.flatMap((component) => (isMoleculeIdInputComponent(component) ? [component.id] : [])))
  );

  const proteinAminoAcidsById = new Map<string, string>();
  if (proteinIds.length > 0) {
    const proteinResult = await doQuerySafe<ProteinAminoAcidsQuery>(
      {query: PROTEIN_AMINO_ACIDS_QUERY},
      {variables: {ids: proteinIds}}
    );
    for (const protein of accessibleObjs(proteinResult.proteins)) {
      proteinAminoAcidsById.set(protein.id, protein.experimentalCurrentVersion?.aminoAcids ?? '');
    }
  }

  const moleculeSmilesById = new Map<string, string>();
  if (moleculeIds.length > 0 && BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE) {
    const moleculeResult = await doQuerySafe<PredictionJobMoleculeSmilesQuery>(
      {query: PREDICTION_JOB_MOLECULE_SMILES_QUERY},
      {variables: {moleculeIds}}
    );
    for (const molecule of accessibleObjs<PredictionJobMoleculeNode>(moleculeResult.molecules)) {
      moleculeSmilesById.set(molecule.id, molecule.currentVersion.chemicalStructure.canonicalSmiles);
    }
  }

  let rawEntryIndex = 0;

  return components.map((component, index) => {
    const entryRawIndex = isRawPredictionInputComponent(component) ? rawEntryIndex++ : null;
    return previewItemFromComponent(
      component,
      index,
      entryRawIndex,
      useNumberedLabels,
      proteinAminoAcidsById,
      moleculeSmilesById
    );
  });
}

function StructurePreviewViewer({downloadUrl}: {downloadUrl: string}): JSX.Element {
  const {classes} = useStyles();

  return (
    <div className={classes.modalViewerWrapper}>
      <MolstarViewer
        url={downloadUrl}
        format={MOLSTAR_PREVIEW_TYPE_TO_FORMAT[EXTERNAL_FILE_TYPE.MOLSTAR_MMCIF_VIEWER]}
        className={classNames('reset', classes.viewer)}
        interactionAnalyticsCallback={() => {
          trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.INTERACTED_MODEL_HUB_PREVIEW_VIEWER);
        }}
      />
    </div>
  );
}

function StructurePreviewActions({
  onExpand,
  onDownload,
  downloadRequesting,
}: {
  onExpand: () => void;
  onDownload: () => void;
  downloadRequesting: boolean;
}): JSX.Element {
  const {classes} = useStyles();

  return (
    <Group
      spacing="sm"
      className={classes.previewActions}
      {...getTestProps({component: 'ResultsPreviewCell', element: 'structurePreviewActions'})}
    >
      <Tooltip label="Open in preview" withinPortal={true}>
        <ActionIcon aria-label="Open in preview" variant="tertiary" onClick={onExpand}>
          <BenchlingIcon icon="expand-right-full" />
        </ActionIcon>
      </Tooltip>
      <Tooltip label="Download structure" withinPortal={true}>
        <ActionIcon
          aria-label="Download structure"
          variant="tertiary"
          onClick={onDownload}
          disabled={downloadRequesting}
        >
          <BenchlingIcon icon="download" />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
}

function DownloadStandardizedOutputButton({
  downloadUrl,
  onRefreshDownloadUrls,
}: {
  downloadUrl: string;
  onRefreshDownloadUrls?: () => void;
}): JSX.Element {
  const onClick = (): void => {
    if (isS3URLExpired(downloadUrl)) {
      onRefreshDownloadUrls?.();
      return;
    }
    window.open(downloadUrl, '_blank', WINDOW_OPEN_FEATURES);
  };

  return (
    <Button variant="primary" onClick={onClick}>
      Download structure
    </Button>
  );
}

function StructurePreviewModal({
  opened,
  onClose,
  downloadUrl,
  onRefreshDownloadUrls,
  sequenceItems,
  sequencesLoading,
}: {
  opened: boolean;
  onClose: () => void;
  downloadUrl: string;
  onRefreshDownloadUrls?: () => void;
  sequenceItems: Array<PreviewSequenceDisplayItem>;
  sequencesLoading: boolean;
}): JSX.Element {
  const {classes} = useStyles();

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Preview"
      size="xl"
      centered={true}
      withinPortal={true}
      classNames={{
        inner: classes.modalInner,
        content: classes.modalContentWrapper,
        body: classes.modalBody,
      }}
    >
      <div className={classes.modalLayout}>
        <div className={classes.modalViewerSection}>
          <StructurePreviewViewer downloadUrl={downloadUrl} />
        </div>
        <div className={classes.modalSequencesSection}>
          {sequencesLoading ? (
            <CenteredHorizontalThrobber />
          ) : sequenceItems.length === 0 ? (
            <Text size="sm" color="dimmed" ta="center">
              No components configured for this job.
            </Text>
          ) : (
            <div className={classes.sequencesTableWrapper}>
              <ScrollArea className={classes.sequencesScrollArea} type="auto">
                <Table className={classes.sequencesTable} verticalSpacing="sm">
                  <thead>
                    <tr>
                      <th className={classes.headerCell}>Component</th>
                      <th className={classes.headerCell}>Sequence / SMILES</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sequenceItems.map((item) => (
                      <tr key={item.key}>
                        <td className={classes.nameColumn}>
                          <PreviewSequenceNameCell item={item} />
                        </td>
                        <td className={classes.sequenceColumn}>{item.text}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ScrollArea>
            </div>
          )}
        </div>
        <div className={classes.modalFooter}>
          <DownloadStandardizedOutputButton
            downloadUrl={downloadUrl}
            onRefreshDownloadUrls={onRefreshDownloadUrls}
          />
        </div>
      </div>
    </Modal>
  );
}

export interface ResultsPreviewCellProps {
  standardizedOutputDownloadUrl: string | null;
  componentApiIds: ReadonlyArray<string>;
  modelName: string;
  configJson?: Record<string, unknown> | null;
  onRefreshDownloadUrls?: () => void;
}

export default function ResultsPreviewCell({
  standardizedOutputDownloadUrl,
  componentApiIds,
  modelName,
  configJson,
  onRefreshDownloadUrls,
}: ResultsPreviewCellProps): JSX.Element {
  const {classes} = useStyles();
  const [modalOpen, setModalOpen] = useState(false);
  const [sequenceItems, setSequenceItems] = useState<Array<PreviewSequenceDisplayItem>>([]);
  const [sequencesLoading, setSequencesLoading] = useState(false);

  const fetchSequenceItems = useCallback((): void => {
    setSequencesLoading(true);
    resolvePreviewSequenceItems(componentApiIds, modelName, configJson)
      .then((items) => {
        setSequenceItems(items);
      })
      .catch(() => {
        setSequenceItems([]);
      })
      .finally(() => {
        setSequencesLoading(false);
      });
  }, [componentApiIds, modelName, configJson]);

  const openPreviewModal = useCallback((): void => {
    if (standardizedOutputDownloadUrl == null) {
      return;
    }
    setModalOpen(true);
    fetchSequenceItems();
  }, [standardizedOutputDownloadUrl, fetchSequenceItems]);

  const closePreviewModal = useCallback((): void => {
    setModalOpen(false);
  }, []);

  const handleDownload = useCallback((): void => {
    if (standardizedOutputDownloadUrl == null) {
      return;
    }
    if (isS3URLExpired(standardizedOutputDownloadUrl)) {
      onRefreshDownloadUrls?.();
      return;
    }
    window.open(standardizedOutputDownloadUrl, '_blank', WINDOW_OPEN_FEATURES);
  }, [onRefreshDownloadUrls, standardizedOutputDownloadUrl]);

  if (standardizedOutputDownloadUrl == null) {
    return <span className={classes.noPreview}>—</span>;
  }

  const modal = (
    <StructurePreviewModal
      opened={modalOpen}
      onClose={closePreviewModal}
      downloadUrl={standardizedOutputDownloadUrl}
      onRefreshDownloadUrls={onRefreshDownloadUrls}
      sequenceItems={sequenceItems}
      sequencesLoading={sequencesLoading}
    />
  );

  return (
    <>
      <StructurePreviewActions
        onExpand={openPreviewModal}
        onDownload={handleDownload}
        downloadRequesting={false}
      />
      {modal}
    </>
  );
}

const useStyles = createStyles((theme) => ({
  previewActions: {
    flexWrap: 'nowrap',
  },
  modalViewerWrapper: {
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    width: '100%',
    height: 'min(560px, 65vh)',
    minHeight: 320,
    maxWidth: '100%',
    overflow: 'hidden',
    borderRadius: theme.radius.md,
    border: `1px solid ${Colors.GRAY_LIGHT}`,
  },
  viewer: {
    borderWidth: 0,
    flex: '1 1 auto',
    minHeight: 0,
    width: '100%',
    height: '100%',
  },
  modalInner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalContentWrapper: {
    margin: 'auto',
    width: 'min(760px, 95vw)',
    maxWidth: '95vw',
  },
  modalBody: {
    paddingTop: theme.spacing.sm,
    paddingBottom: 0,
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    borderTop: `1px solid ${Colors.GRAY_LIGHT}`,
    marginTop: theme.spacing.lg,
    marginLeft: -theme.spacing.l,
    marginRight: -theme.spacing.l,
    marginBottom: -theme.spacing.l,
    paddingTop: theme.spacing.lg,
    paddingBottom: theme.spacing.lg,
    paddingLeft: theme.spacing.l,
    paddingRight: theme.spacing.l,
  },
  noPreview: {
    color: Colors.GRAY_DARKER,
    fontSize: theme.fontSizes.sm,
  },
  modalLayout: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing.lg,
    width: '100%',
  },
  modalViewerSection: {
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'stretch',
    width: '100%',
  },
  modalSequencesSection: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  sequencesTableWrapper: {
    boxSizing: 'border-box',
    width: '100%',
    border: `1px solid ${Colors.GRAY_LIGHT}`,
    borderRadius: theme.radius.md,
    overflow: 'hidden',
  },
  sequencesScrollArea: {
    width: '100%',
    maxHeight: 280,
  },
  sequencesTable: {
    width: '100%',
    tableLayout: 'fixed',
    '& tbody tr:not(:last-child) td': {
      borderBottom: `1px solid ${Colors.GRAY_LIGHT}`,
    },
  },
  headerCell: {
    textAlign: 'left',
    fontSize: theme.fontSizes.sm,
    fontWeight: 600,
    paddingTop: theme.spacing.xs,
    paddingBottom: theme.spacing.xs,
    paddingLeft: theme.spacing.md,
    paddingRight: theme.spacing.md,
    borderBottom: `1px solid ${Colors.GRAY_LIGHT}`,
  },
  nameColumn: {
    width: '35%',
    verticalAlign: 'top',
    paddingTop: theme.spacing.xs,
    paddingBottom: theme.spacing.xs,
    paddingLeft: theme.spacing.md,
    paddingRight: theme.spacing.md,
    fontSize: theme.fontSizes.sm,
    lineHeight: 1.4,
    borderRight: `1px solid ${Colors.GRAY_LIGHT}`,
  },
  sequenceColumn: {
    verticalAlign: 'top',
    paddingTop: theme.spacing.xs,
    paddingBottom: theme.spacing.xs,
    paddingLeft: theme.spacing.md,
    paddingRight: theme.spacing.md,
    fontSize: theme.fontSizes.sm,
    lineHeight: 1.4,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
}));
